import { useCallback, useEffect, useRef, useState } from "react";
import {
  Plus,
  ShieldCheck,
  Trash,
  FileText,
  Upload,
  CheckCircle,
  XCircle,
  Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { listPolicies, createPolicy, deletePolicy } from "../lib/api";

const PLATFORM_TYPES = [
  { id: "amazon-associates", label: "Amazon Associates" },
  { id: "shareasale", label: "ShareASale" },
  { id: "clickbank", label: "ClickBank" },
  { id: "impact", label: "Impact / CJ" },
  { id: "meta-ads", label: "Meta Ads Policy" },
  { id: "google-ads", label: "Google Ads Policy" },
  { id: "tiktok-ads", label: "TikTok Ads Policy" },
  { id: "custom", label: "Custom / Other" },
];

function PolicyDetail({ policy }) {
  const r = policy.rules || {};
  return (
    <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-1 md:grid-cols-2 gap-4">
      {r.summary && (
        <div className="md:col-span-2">
          <div className="label-mono mb-1">summary</div>
          <div className="text-sm text-zinc-300 leading-relaxed">{r.summary}</div>
        </div>
      )}
      {r.forbidden_claims?.length > 0 && (
        <div>
          <div className="label-mono mb-1 text-red-400">forbidden claims</div>
          <ul className="text-xs text-zinc-300 space-y-1 leading-relaxed">
            {r.forbidden_claims.map((x, i) => (
              <li key={`fc-${i}`}>· {x}</li>
            ))}
          </ul>
        </div>
      )}
      {r.forbidden_words?.length > 0 && (
        <div>
          <div className="label-mono mb-1 text-red-400">forbidden words</div>
          <div className="flex flex-wrap gap-1">
            {r.forbidden_words.map((w, i) => (
              <span
                key={`fw-${i}`}
                className="font-mono text-[10px] px-2 py-1 bg-red-500/10 border border-red-500/30 text-red-300"
              >
                {w}
              </span>
            ))}
          </div>
        </div>
      )}
      {r.required_disclosures?.length > 0 && (
        <div>
          <div className="label-mono mb-1 text-[#ccff00]">required disclosures</div>
          <ul className="text-xs text-zinc-300 space-y-1 leading-relaxed">
            {r.required_disclosures.map((x, i) => (
              <li key={`rd-${i}`}>· {x}</li>
            ))}
          </ul>
        </div>
      )}
      {r.donts?.length > 0 && (
        <div>
          <div className="label-mono mb-1">don&apos;ts</div>
          <ul className="text-xs text-zinc-400 space-y-1 leading-relaxed">
            {r.donts.slice(0, 6).map((x, i) => (
              <li key={`dnt-${i}`}>· {x}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CompliancePolicies() {
  const [policies, setPolicies] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [platformType, setPlatformType] = useState("amazon-associates");
  const [rawText, setRawText] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const fileInputRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const p = await listPolicies();
      setPolicies(p);
    } catch (err) {
      console.error("Failed to load policies:", err);
      toast.error("Failed to load policies");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const doCreate = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (!file && !rawText.trim())
      return toast.error("Upload a file or paste the T&C text");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("platform_type", platformType);
      if (file) fd.append("file", file);
      if (rawText.trim()) fd.append("raw_text", rawText.trim());
      await createPolicy(fd);
      toast.success("Policy saved · rules extracted");
      setName("");
      setRawText("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setOpen(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this policy?")) return;
    await deletePolicy(id);
    refresh();
    toast.success("Policy deleted");
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="label-mono mb-3">compliance</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tighter">
            Policy <span className="font-black text-[#ccff00]">Library</span>
          </h1>
          <p className="text-zinc-400 mt-3 max-w-lg leading-relaxed">
            Upload affiliate program T&amp;Cs or platform ad policies. AdForge
            extracts enforceable rules and injects them into every ad it
            generates — plus flags violations post-hoc.
          </p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-policy-btn"
              className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-semibold px-6 py-3 flex items-center gap-2 transition-colors"
            >
              <Plus size={18} weight="bold" />
              Upload policy
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border border-white/10 rounded-none max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                Add compliance policy
              </DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm">
                Paste the T&C text or upload the policy PDF/DOCX/TXT. We&apos;ll extract enforceable rules.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div>
                <div className="label-mono mb-2">name</div>
                <Input
                  data-testid="policy-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Amazon Associates Operating Agreement"
                  className="rounded-none bg-[#121212] border-white/10 h-11"
                />
              </div>
              <div>
                <div className="label-mono mb-2">platform type</div>
                <Select value={platformType} onValueChange={setPlatformType}>
                  <SelectTrigger
                    data-testid="policy-platform"
                    className="rounded-none bg-[#121212] border-white/10 h-11"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 rounded-none">
                    {PLATFORM_TYPES.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="label-mono mb-2">upload file (PDF, DOCX, TXT)</div>
                <label className="border border-dashed border-white/20 hover:border-[#ccff00] transition-colors p-4 flex items-center gap-3 cursor-pointer bg-[#121212]">
                  <Upload size={20} className="text-zinc-400" />
                  <input
                    ref={fileInputRef}
                    data-testid="policy-file"
                    type="file"
                    accept=".pdf,.docx,.txt"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  <span className="text-sm text-zinc-300 truncate">
                    {file ? file.name : "Choose file — or paste text below"}
                  </span>
                </label>
              </div>
              <div>
                <div className="label-mono mb-2">or paste raw T&amp;C text</div>
                <textarea
                  data-testid="policy-text"
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder="Paste the terms & conditions text here…"
                  rows={6}
                  className="w-full rounded-none bg-[#121212] border border-white/10 p-3 font-mono text-xs text-zinc-200 focus:outline-none focus:border-[#ccff00]"
                />
              </div>
            </div>
            <DialogFooter>
              <button
                data-testid="save-policy-btn"
                onClick={doCreate}
                disabled={loading}
                className="bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 px-6 py-2.5 font-semibold w-full"
              >
                {loading ? "Extracting rules with AI…" : "Save & extract rules"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3" data-testid="policies-list">
        {policies.length === 0 && (
          <div className="bg-[#0a0a0a] border border-white/10 px-6 py-16 text-center text-zinc-500">
            <ShieldCheck size={40} className="mx-auto mb-3 opacity-40" />
            <div className="font-mono text-sm">
              no policies yet — upload one to make every generated ad compliant
            </div>
          </div>
        )}

        {policies.map((p) => {
          const isOpen = expandedId === p.id;
          return (
            <div
              key={p.id}
              data-testid={`policy-${p.id}`}
              className="bg-[#0a0a0a] border border-white/10 hover:border-white/20 transition-colors"
            >
              <div className="px-6 py-4 flex items-center justify-between gap-4">
                <button
                  onClick={() => setExpandedId(isOpen ? null : p.id)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                >
                  <FileText size={18} className="text-[#ccff00] flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold truncate">{p.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500 truncate">
                      {p.platform_type}
                      {p.source_file_name ? ` · ${p.source_file_name}` : ""} ·{" "}
                      {p.raw_text_length.toLocaleString()} chars
                    </div>
                  </div>
                </button>
                <div className="flex gap-4 items-center flex-shrink-0">
                  <div className="hidden md:flex gap-4 text-center">
                    <div>
                      <div className="font-mono text-lg font-bold text-red-400">
                        {p.rules?.forbidden_claims?.length || 0}
                      </div>
                      <div className="label-mono text-[9px]">forbidden</div>
                    </div>
                    <div>
                      <div className="font-mono text-lg font-bold text-[#ccff00]">
                        {p.rules?.required_disclosures?.length || 0}
                      </div>
                      <div className="label-mono text-[9px]">disclosures</div>
                    </div>
                  </div>
                  <button
                    onClick={() => remove(p.id)}
                    data-testid={`delete-policy-${p.id}`}
                    className="p-2 bg-[#121212] border border-white/10 hover:border-red-500 hover:text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              {isOpen && (
                <div className="px-6 pb-6" data-testid={`policy-detail-${p.id}`}>
                  <PolicyDetail policy={p} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 bg-[#0a0a0a] border border-white/10 p-6 flex items-start gap-4">
        <ShieldCheck size={22} className="text-[#ccff00] flex-shrink-0 mt-1" weight="duotone" />
        <div className="text-sm text-zinc-300 leading-relaxed">
          <div className="font-display font-bold mb-1">How compliance is enforced</div>
          <ol className="text-xs text-zinc-400 space-y-1 list-decimal list-inside">
            <li>
              At generation time, attached policies&apos; forbidden claims/words + required disclosures are injected into the LLM system prompt.
            </li>
            <li>
              After generation, a &ldquo;Run compliance check&rdquo; button runs a second-pass audit that flags every violation with severity, exact quote, and a compliant rewrite.
            </li>
            <li>
              Violations use color coding: <CheckCircle size={12} weight="fill" className="inline text-[#ccff00] mx-1" /> pass · <Warning size={12} weight="fill" className="inline text-amber-400 mx-1" /> warn · <XCircle size={12} weight="fill" className="inline text-red-400 mx-1" /> fail.
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
